/**
 * Chat User Settings Form
 * User preferences for chat module - AI model category and personality/tone
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Separator } from '../../../components/ui/separator';
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { EntityLoadState, BidiText } from '../../../shared/components/common';
import { useApiError } from '../../../shared/hooks/useApiError';
import { useNotification } from '../../../shared/context/NotificationContext';
import { useChatUserSettings, useUpdateChatUserSettings, useModelCategories } from '../hooks/useChatSettings';
import { usePromptConfig, useUpdatePromptConfig } from '../../../shared/ai_chat/hooks/useChat';
import type { PromptConfig } from '../../../shared/ai_chat/services/chatApi';

interface FormData {
  preferred_model_category: string;
}

type PartMode = 'none' | 'preset' | 'custom';

// Only show personality and tone parts for user customization
const USER_CUSTOMIZABLE_PARTS = ['personality', 'tone'];

interface ChatUserSettingsFormProps {
  onSuccess?: () => void;
}

export function ChatUserSettingsForm({ onSuccess }: ChatUserSettingsFormProps) {
  const { t } = useTranslation(['aiChat', 'common']);
  const { showSuccess } = useNotification();
  const { handleMutationError } = useApiError();

  // Model category settings
  const { data: settings, isLoading, error } = useChatUserSettings();
  const { data: categoriesData, isLoading: categoriesLoading } = useModelCategories();
  const updateMutation = useUpdateChatUserSettings();

  // Prompt config (personality/tone) settings
  const { data: promptConfigResponse, isLoading: promptConfigLoading, error: promptConfigError } = usePromptConfig();
  const updatePromptConfigMutation = useUpdatePromptConfig();
  const [localPromptConfig, setLocalPromptConfig] = useState<PromptConfig>({});
  const [promptConfigInitialized, setPromptConfigInitialized] = useState(false);

  const { control, handleSubmit, reset, formState: { isDirty }, watch } = useForm<FormData>({
    defaultValues: {
      preferred_model_category: 'system_default',
    },
  });

  const selectedCategory = watch('preferred_model_category');

  // Build category options from API response
  const categoryOptions = useMemo(() => {
    const options: Array<{
      value: string;
      name: string;
      description: string;
      selectedModel: string | null;
    }> = [
      { value: 'system_default', name: t('aiChat:settings.userSettings.modelCategories.systemDefault'), description: '', selectedModel: null },
    ];

    if (categoriesData?.categories) {
      for (const cat of categoriesData.categories) {
        options.push({
          value: cat.id,
          name: cat.name,
          description: cat.description,
          selectedModel: cat.selected_model,
        });
      }
    }

    return options;
  }, [categoriesData, t]);

  // Get effective model display text
  const effectiveModelText = useMemo(() => {
    if (!settings?.effective_model_category || !categoriesData?.categories) {
      return null;
    }

    const category = categoriesData.categories.find(c => c.id === settings.effective_model_category);
    if (category?.selected_model) {
      return `${category.name} (${category.selected_model})`;
    }
    return settings.effective_model_category;
  }, [settings, categoriesData]);

  // Filter parts to only show personality and tone
  const promptConfigData = promptConfigResponse?.data;
  const availableParts = useMemo(() => {
    return (promptConfigData?.available_parts || []).filter(
      (part) => USER_CUSTOMIZABLE_PARTS.includes(part.id)
    );
  }, [promptConfigData]);
  const savedPromptConfig = useMemo(() => promptConfigData?.prompt_config || {}, [promptConfigData]);

  // Build a map of presets by ID for quick lookup
  const presetsById = useMemo(() => {
    const map: Record<string, { id: string; name: string; content: string }> = {};
    availableParts.forEach((part) => {
      (part.presets || []).forEach((preset) => {
        map[preset.id] = preset;
      });
    });
    return map;
  }, [availableParts]);

  // Get current state for a prompt part
  const getPartState = (partId: string): { mode: PartMode; presetId: string | null; customText: string } => {
    const partConfig = localPromptConfig[partId];
    if (!partConfig) {
      return { mode: 'none', presetId: null, customText: '' };
    }
    // Check for custom mode - custom key exists (even if empty string)
    if (partConfig.custom !== undefined) {
      return { mode: 'custom', presetId: null, customText: partConfig.custom || '' };
    }
    if (partConfig.preset_id) {
      return { mode: 'preset', presetId: partConfig.preset_id, customText: '' };
    }
    return { mode: 'none', presetId: null, customText: '' };
  };

  // Check if prompt config has unsaved changes
  const promptConfigHasChanges = JSON.stringify(localPromptConfig) !== JSON.stringify(savedPromptConfig);

  // Reset form when settings load
  useEffect(() => {
    if (settings) {
      reset({
        preferred_model_category: settings.preferred_model_category ?? 'system_default',
      });
    }
  }, [settings, reset]);

  // Initialize local prompt config when data loads
  useEffect(() => {
    if (promptConfigData && !promptConfigInitialized) {
      setLocalPromptConfig(savedPromptConfig);
      setPromptConfigInitialized(true);
    }
  }, [promptConfigData, savedPromptConfig, promptConfigInitialized]);

  // Prompt config handlers
  const handlePartModeChange = (partId: string, newMode: PartMode) => {
    const newConfig = { ...localPromptConfig };
    if (newMode === 'none') {
      delete newConfig[partId];
    } else if (newMode === 'custom') {
      newConfig[partId] = { custom: '' };
    } else {
      // Preset mode - select first available preset
      const part = availableParts.find((p) => p.id === partId);
      const firstPreset = part?.presets?.[0];
      if (firstPreset) {
        newConfig[partId] = { preset_id: firstPreset.id };
      } else {
        delete newConfig[partId];
      }
    }
    setLocalPromptConfig(newConfig);
  };

  const handlePresetChange = (partId: string, presetId: string) => {
    setLocalPromptConfig({
      ...localPromptConfig,
      [partId]: { preset_id: presetId },
    });
  };

  const handleCustomTextChange = (partId: string, text: string) => {
    setLocalPromptConfig({
      ...localPromptConfig,
      [partId]: { custom: text },
    });
  };

  const onSubmit = async (data: FormData) => {
    try {
      // Save model category settings
      await updateMutation.mutateAsync(data);

      // Save prompt config if changed
      if (promptConfigHasChanges) {
        await updatePromptConfigMutation.mutateAsync(localPromptConfig);
      }

      showSuccess(t('aiChat:settings.saveSuccess'));
      onSuccess?.();
    } catch (error) {
      handleMutationError(error);
    }
  };

  if (isLoading || categoriesLoading || promptConfigLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || promptConfigError) {
    return (
      <EntityLoadState error={error} className="mb-4" />
    );
  }

  const hasAnyChanges = isDirty || promptConfigHasChanges;
  const isSaving = updateMutation.isPending || updatePromptConfigMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-6">
        {categoriesData?.provider && (
          <p className="text-sm text-muted-foreground">
            {t('aiChat:settings.userSettings.provider', { name: categoriesData.provider.name })}
          </p>
        )}

        <Controller
          name="preferred_model_category"
          control={control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label>{t('aiChat:settings.userSettings.preferredModelCategory')}</Label>
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={!settings?.allow_user_model_choice}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div>
                        <div>
                          {option.selectedModel
                            ? `${option.name} (${option.selectedModel})`
                            : option.name}
                        </div>
                        {option.description && (
                          <div className="text-xs text-muted-foreground">
                            {option.description}
                          </div>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {!settings?.allow_user_model_choice
                  ? t('aiChat:settings.userSettings.modelChoiceDisabled')
                  : t('aiChat:settings.userSettings.modelCategoryHelp')}
              </p>
            </div>
          )}
        />

        {effectiveModelText && selectedCategory === 'system_default' && (
          <p className="text-sm text-muted-foreground">
            {t('aiChat:settings.userSettings.effectiveModel', {
              model: effectiveModelText,
            })}
          </p>
        )}

        {/* Personality & Tone Settings */}
        {availableParts.length > 0 && (
          <>
            <Separator className="my-4" />
            <p className="text-sm text-muted-foreground mb-4">
              {t('aiChat:settings.description')}
            </p>

            {availableParts.map((part) => {
              const { mode, presetId, customText } = getPartState(part.id);
              const presets = part.presets || [];
              const selectedPreset = presetId ? presetsById[presetId] : null;

              return (
                <div key={part.id} className="space-y-3">
                  <h4 className="text-sm font-medium"><BidiText>{part.name}</BidiText></h4>
                  {part.description && (
                    <p className="text-sm text-muted-foreground">
                      {part.description}
                    </p>
                  )}

                  <ToggleGroup
                    type="single"
                    value={mode}
                    onValueChange={(value) => value && handlePartModeChange(part.id, value as PartMode)}
                    className="justify-start"
                  >
                    <ToggleGroupItem value="none" size="sm">
                      {t('aiChat:settings.default')}
                    </ToggleGroupItem>
                    <ToggleGroupItem value="preset" size="sm" disabled={presets.length === 0}>
                      {t('aiChat:settings.usePreset')}
                    </ToggleGroupItem>
                    <ToggleGroupItem value="custom" size="sm">
                      {t('aiChat:settings.custom')}
                    </ToggleGroupItem>
                  </ToggleGroup>

                  {mode === 'preset' && (
                    <div className="space-y-2">
                      <Select
                        value={presetId || 'none'}
                        onValueChange={(v) => v !== 'none' && handlePresetChange(part.id, v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('aiChat:settings.selectPreset')} />
                        </SelectTrigger>
                        <SelectContent>
                          {presets.map((preset) => (
                            <SelectItem key={preset.id} value={preset.id}>
                              {preset.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedPreset && (
                        <div className="p-3 bg-muted rounded-md">
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {selectedPreset.content}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {mode === 'custom' && (
                    <Textarea
                      value={customText}
                      onChange={(e) => handleCustomTextChange(part.id, e.target.value)}
                      placeholder={t('aiChat:settings.customPlaceholder')}
                      rows={3}
                    />
                  )}
                </div>
              );
            })}
          </>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button
            type="submit"
            disabled={!hasAnyChanges || isSaving}
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common:app.save')}
          </Button>
        </div>
      </div>
    </form>
  );
}
